import { useState, useEffect } from "react";
import { X, Copy, Check, Layers } from "lucide-react";
import { api } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";

export default function FormulationDetailModal({ formulation, onClose }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedIngredients, setCopiedIngredients] = useState(false);
  const [copiedInstructions, setCopiedInstructions] = useState(false);
  const [copiedFull, setCopiedFull] = useState(false);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/recipes/${formulation.id}`);
        if (response.data.code === 200) {
          setDetail(response.data.data);
        }
      } catch (error) {
        console.error("Fetch Error:", error.response?.data || error.message);
        setDetail(formulation);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [formulation.id]);

  const data = detail || formulation;

  const ingredientsList = Array.isArray(data.ingredients)
    ? data.ingredients
    : [];

  const ingredientsAsText = ingredientsList
    .map((ing) => {
      const parts = [
        ing.chemical_name,
        ing.trade_name ? `(${ing.trade_name})` : "",
        ing.percentage != null ? `${ing.percentage}%` : "",
        ing.notes ? `- ${ing.notes}` : "",
      ].filter(Boolean);
      return parts.join(" ");
    })
    .join("\n");

  const handleCopy = (text, setter) => {
    navigator.clipboard?.writeText(text || "");
    setter(true);
    setTimeout(() => setter(false), 1800);
  };

  const handleCopyFull = () => {
    const full = [
      `${t("formulations.name")}: ${data.name}`,
      data.description
        ? `${t("formulations.description")}: ${data.description}`
        : "",
      ingredientsList.length > 0
        ? `${t("formulations.ingredients")}:\n${ingredientsAsText}`
        : "",
      data.preparation_instructions
        ? `${t("formulations.instructions")}: ${data.preparation_instructions}`
        : "",
    ]
      .filter(Boolean)
      .join("\n\n");
    handleCopy(full, setCopiedFull);
  };

  const infoIconStyle = {
    width: "34px",
    height: "34px",
    minWidth: "34px",
    background: "rgba(12,104,57,0.1)",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0c6839",
    flexShrink: 0,
  };

  const copyBtnStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#0c6839",
    background: "rgba(12,104,57,0.08)",
    border: "none",
    borderRadius: "8px",
    padding: "7px 14px",
    cursor: "pointer",
    marginTop: "10px",
    fontFamily: "inherit",
  });

  const thStyle = {
    fontSize: "12px",
    fontWeight: "700",
    color: "#0c6839",
    background: "rgba(12,104,57,0.08)",
    padding: "10px 10px",
    textAlign: isRtl ? "right" : "left",
    whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(12,104,57,0.15)",
  };

  const tdStyle = {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1a1f1c",
    padding: "10px 10px",
    borderBottom: "1px solid #eef1ee",
    verticalAlign: "top",
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      dir={isRtl ? "rtl" : "ltr"}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          maxWidth: "540px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "14px",
            [isRtl ? "left" : "right"]: "14px",
            zIndex: 10,
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <X size={16} />
        </button>

        <div
          style={{
            height: "180px",
            background: "rgba(12,104,57,0.06)",
            borderRadius: "24px 24px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {data.image ? (
            <img
              src={data.image}
              alt={data.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(12,104,57,0.3)"
              strokeWidth="1.2"
            >
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
            </svg>
          )}
        </div>

        <div
          style={{
            padding: "28px",
            boxSizing: "border-box",
            flex: 1,
            textAlign: isRtl ? "right" : "left",
          }}
        >
          {loading ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: i === 0 ? "28px" : "60px",
                    borderRadius: "10px",
                    background:
                      "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s infinite",
                    width: i === 0 ? "60%" : "100%",
                  }}
                />
              ))}
            </div>
          ) : (
            <>
              <h2
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#0c6839",
                  marginBottom: "10px",
                  marginTop: 0,
                }}
              >
                {data.name}
              </h2>

              {data.type && (
                <span
                  style={{
                    display: "inline-block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#0c6839",
                    background: "rgba(12,104,57,0.1)",
                    border: "1px solid rgba(12,104,57,0.2)",
                    padding: "5px 14px",
                    borderRadius: "20px",
                    marginBottom: "22px",
                  }}
                >
                  {data.type.name}
                </span>
              )}

              {data.description && (
                <div style={{ marginBottom: "20px" }}>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#9ca3af",
                      marginBottom: "6px",
                      marginTop: 0,
                    }}
                  >
                    {t("formulations.description")} :
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1a1f1c",
                      lineHeight: "1.7",
                      margin: 0,
                    }}
                  >
                    {data.description}
                  </p>
                </div>
              )}

              {ingredientsList.length > 0 && (
                <div style={{ marginBottom: "20px" }}>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#9ca3af",
                      marginBottom: "6px",
                      marginTop: 0,
                    }}
                  >
                    {t("formulations.ingredients")}
                  </p>

                  <div
                    style={{
                      overflowX: "auto",
                      borderRadius: "10px",
                      border: "1px solid #eef1ee",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "480px",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={thStyle}>
                            {t("formulations.chemicalName", "Chemical Name")}
                          </th>
                          <th style={thStyle}>
                            {t("formulations.tradeName", "Trade Name")}
                          </th>
                          <th style={{ ...thStyle, textAlign: "center" }}>%</th>
                          <th style={thStyle}>
                            {t("formulations.notes", "Notes")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingredientsList.map((ing, idx) => (
                          <tr
                            key={ing.id}
                            style={{
                              background: idx % 2 === 0 ? "#ffffff" : "#f8faf8",
                            }}
                          >
                            <td style={{ ...tdStyle, fontWeight: "700" }}>
                              {ing.chemical_name}
                            </td>
                            <td style={tdStyle}>{ing.trade_name || "-"}</td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "center",
                                color: "#0c6839",
                                fontWeight: "700",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {ing.percentage != null
                                ? `${ing.percentage}%`
                                : "-"}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                color: "#6b7280",
                                fontWeight: "500",
                              }}
                            >
                              {ing.notes || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <button
                    style={copyBtnStyle(copiedIngredients)}
                    onClick={() =>
                      handleCopy(ingredientsAsText, setCopiedIngredients)
                    }
                  >
                    {copiedIngredients ? (
                      <Check size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                    {t("common.copy")}
                  </button>
                </div>
              )}

              {data.preparation_instructions && (
                <div style={{ marginBottom: "24px" }}>
                  <p
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#9ca3af",
                      marginBottom: "6px",
                      marginTop: 0,
                    }}
                  >
                    {t("formulations.instructions")}
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1a1f1c",
                      lineHeight: "1.7",
                      margin: 0,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {data.preparation_instructions}
                  </p>
                  <button
                    style={copyBtnStyle(copiedInstructions)}
                    onClick={() =>
                      handleCopy(
                        data.preparation_instructions,
                        setCopiedInstructions,
                      )
                    }
                  >
                    {copiedInstructions ? (
                      <Check size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                    {t("common.copy")}
                  </button>
                </div>
              )}

              {data.categories?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    background: "#f8faf8",
                    borderRadius: "12px",
                    padding: "13px 16px",
                    marginBottom: "20px",
                  }}
                >
                  <span style={infoIconStyle}>
                    <Layers size={16} />
                  </span>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#9ca3af",
                        display: "block",
                      }}
                    >
                      {t("formulations.categoriesLabel")}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#1a1f1c",
                        display: "block",
                      }}
                    >
                      {data.categories.map((c) => c.name).join(" - ")}
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={handleCopyFull}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  width: "100%",
                  background: "#0c6839",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "14px",
                  padding: "15px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "background 0.18s",
                  fontFamily: "inherit",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#0a5630")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#0c6839")
                }
              >
                {copiedFull ? <Check size={17} /> : <Copy size={17} />}
                {copiedFull ? t("common.copied") : t("formulations.copyFull")}
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
